/**
 * BffClient — 前端访问后端的唯一入口
 * 对齐 api-unified.md §一 BffClient
 *
 * 请求自动带 cookie（credentials: include）
 * 401 时自动刷新 token
 *
 * 9.21 离线重试队列：fetch 抛出（网络错误）且离线时，
 * 把请求序列化存入 localStorage，等恢复在线后重放。
 */

// #5 默认改为相对路径 /api/v1：
// - 开发环境：vite proxy 把 /api 转发到 http://localhost:61015（见 vite.config.ts）
// - 生产环境：通过 CF/DNS 指向真实 API 服务器（同域或 CDN）
// 仍可用 VITE_API_BASE 环境变量覆盖绝对地址。
const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// ============================================================================
// 9.21 离线重试队列（localStorage 持久化）
// ============================================================================

const PENDING_KEY = 'hump-pending-requests';

/** 可序列化的请求描述 */
export interface PendingRequest {
  id: string;
  path: string;
  method: string;
  body?: string;
  /** 创建时间，用于排序 */
  createdAt: number;
}

/** 读取待重放队列 */
export function getPendingRequests(): PendingRequest[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as PendingRequest[];
  } catch {
    return [];
  }
}

/** 写入待重放队列 */
function setPendingRequests(list: PendingRequest[]): void {
  try {
    if (list.length === 0) {
      localStorage.removeItem(PENDING_KEY);
    } else {
      localStorage.setItem(PENDING_KEY, JSON.stringify(list));
    }
  } catch {
    // 配额满等异常，静默
  }
}

/** 把一条失败请求加入队列（仅 GET 之外需要持久化的方法） */
function enqueuePending(path: string, options: RequestInit): void {
  const method = (options.method || 'GET').toUpperCase();
  // GET 请求不入队（幂等，重新触发即可）
  if (method === 'GET') return;
  const body = typeof options.body === 'string' ? options.body : undefined;
  const list = getPendingRequests();
  list.push({
    id: `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    path,
    method,
    body,
    createdAt: Date.now(),
  });
  setPendingRequests(list);
}

/** 在线时重放队列，返回成功重放的条数 */
export async function replayPendingRequests(): Promise<number> {
  const list = getPendingRequests();
  if (list.length === 0) return 0;

  const remaining: PendingRequest[] = [];
  let replayed = 0;
  for (const item of list) {
    try {
      await fetch(`${API_BASE}${item.path}`, {
        method: item.method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: item.body,
      });
      replayed++;
    } catch {
      // 仍失败 → 保留
      remaining.push(item);
    }
  }
  setPendingRequests(remaining);
  return replayed;
}

// ============================================================================

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  } catch (e) {
    // 网络错误：离线时把写请求存入重试队列
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      enqueuePending(path, options);
    }
    throw e;
  }

  // 401 → 尝试刷新 token
  if (res.status === 401 && !path.startsWith('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      // 重试原请求
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      return handleResponse<T>(retryRes);
    }
    throw new Error('UNAUTHORIZED');
  }

  return handleResponse<T>(res);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const data = await res.json();
  if (!data.ok) {
    throw new Error(data.error?.message || 'Request failed');
  }
  return data.data;
}

async function tryRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return true;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
    }
  })();
  return refreshPromise;
}

export const bffClient = {
  auth: {
    register: (email: string, password: string, displayName?: string) =>
      request<{ user: any }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName }),
      }),

    login: (email: string, password: string) =>
      request<{ user: any }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    logout: () =>
      request('/auth/logout', { method: 'POST' }),

    me: () =>
      request<{ user: any; entitlements: any; usage: any }>('/auth/me'),
  },

  sub: {
    plans: () => request<{ plans: any[] }>('/sub/plans'),
  },

  sync: {
    status: () => request('/sync/status'),
    push: (changes: any) =>
      request('/sync/push', {
        method: 'POST',
        body: JSON.stringify(changes),
      }),
    pull: (cursor: string) =>
      request('/sync/pull', {
        method: 'POST',
        body: JSON.stringify({ cursor }),
      }),
    // 11.6 冲突裁决：提交每个冲突块的解决方案
    resolveConflicts: (resolutions: { blockId: string; resolution: 'local' | 'remote' | 'skip' }[]) =>
      request('/sync/resolve-conflicts', {
        method: 'POST',
        body: JSON.stringify({ resolutions }),
      }),
  },

  ai: {
    translate: (text: string, sourceLang: string, targetLang: string) =>
      request('/ai/translate', {
        method: 'POST',
        body: JSON.stringify({ text, sourceLang, targetLang }),
      }),
    models: () => request<{ models: any[] }>('/ai/models'),
  },

  voice: {
    models: () => request('/voice/models'),
    synthesize: (modelId: string, text: string, rate = 1, pitch = 1) =>
      request('/voice/synthesize', {
        method: 'POST',
        body: JSON.stringify({ modelId, text, rate, pitch }),
      }),
  },

  import: {
    /** 抓取网页 HTML（服务端代理，绕过 CORS），返回 { url, html, title } */
    fetchUrl: (url: string) =>
      request<{ url: string; html: string; title: string }>('/import/url', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
  },
};
